/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { LayoutGridIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { Link } from "react-router"

import type { PageApplicationId } from "../../features/application/application-types"
import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { AppRoutes } from "../../routes/routes"
import { prepareUrlWithFlags } from "../../utils/prepare-application-url"
import { useViewModel } from "../app/app-hooks"
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator
} from "../primitives/breadcrumb"

interface Props {
    /**
     * Whether the current view is the device detail view.
     *
     * The detail view adds the trailing `Detail` crumb and makes the list crumb
     * link back to the filtered list. The list view leaves the prop off because
     * the current page should not link to itself.
     *
     * @default false
     */
    isDetail?: boolean
    /**
     * Current device-list state, when a list has been loaded.
     *
     * Used to rebuild the filtered list URL when navigating back from the detail
     * view. It also provides the application type used in the list crumb label.
     *
     * `null` on a direct detail-page load because the detail response does not
     * include the application type. In that case the list crumb stays as plain
     * text because its URL cannot be reconstructed.
     */
    selectedPageId: PageApplicationId | null
}

/**
 * Breadcrumbs for the device list and detail views.
 *
 * The list crumb includes the application type name when available. The name
 * comes from the application-type store, so the component observes it in case
 * the store loads after the breadcrumb first renders.
 *
 * @example
 * <ApplicationBreadcrumb selectedPageId={vm.selectedPageId} />
 * <ApplicationBreadcrumb isDetail selectedPageId={appVM.selectedPageId} />
 */
export const ApplicationBreadcrumb = observer(({ isDetail = false, selectedPageId }: Props) => {
    const appTypeVM = useViewModel(ApplicationTypeViewModel)
    const appDescription = appTypeVM.appTypeFor(selectedPageId?.appTypeId ?? null)?.description

    // Use a middle dot (U+00B7) between the list label and application type.
    const listLabel = appDescription ? `Device List · ${appDescription}` : "Device List"
    const listUrl = selectedPageId
        ? prepareUrlWithFlags(
              AppRoutes.Application,
              selectedPageId.status,
              selectedPageId.appTypeId,
              selectedPageId.flags
          )
        : null

    return (
        <Breadcrumb>
            <BreadcrumbList className="m-0 p-0 sm:gap-1.5">
                <BreadcrumbItem>
                    <BreadcrumbLink render={<Link to={AppRoutes.Dashboard} aria-label="Dashboard" />}>
                        <LayoutGridIcon className="size-4" />
                    </BreadcrumbLink>
                </BreadcrumbItem>

                {(selectedPageId || isDetail) && (
                    <>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            {/*
                             * Only the detail view links back to the list. Without a selected
                             * page there is no application type to reconstruct the list URL from,
                             * so keep the crumb visible as plain text rather than removing it.
                             */}
                            {isDetail && listUrl ? (
                                <BreadcrumbLink render={<Link to={listUrl} />}>{listLabel}</BreadcrumbLink>
                            ) : (
                                <BreadcrumbPage>{listLabel}</BreadcrumbPage>
                            )}
                        </BreadcrumbItem>
                    </>
                )}

                {isDetail && (
                    <>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Detail</BreadcrumbPage>
                        </BreadcrumbItem>
                    </>
                )}
            </BreadcrumbList>
        </Breadcrumb>
    )
})
